import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { UploadFileOutlined as UploadIcon } from '@mui/icons-material';
import { FileFieldActions } from '../../../../detail-form/FileFieldActions';
import {
  useDeleteFileMutation,
  useLazyGetDirectoryQuery,
  useUploadFieldFileMutation,
} from '../../../../file-storage/api/fileStorageApi';
import { extractErrorMessage } from '../../../../../utils/errorUtils';
import type { ColumnMetadata } from '../../../types';

type FileCellProps = {
  fileGuid?: string | null;
  isEditing?: boolean;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  metadata?: ColumnMetadata;
  onValueChange?: (guid: string | null) => void;
};

const DEFAULT_UPLOAD_FOLDER = 'uploads';

const getAcceptAttribute = (metadata?: ColumnMetadata): string | undefined => {
  if (!metadata) return undefined;
  const { allowedTypes, extension } = metadata as Record<string, unknown>;
  if (Array.isArray(allowedTypes)) {
    return allowedTypes.filter((type): type is string => typeof type === 'string').join(',');
  }
  return typeof extension === 'string' ? extension : undefined;
};

/**
 * Grid cell wrapper around the shared download/view actions.
 * Reuses FileFieldActions so download/view go through the same
 * authenticated RTK Query flow used in the detail form (adds
 * the Bearer token via prepareHeaders — a plain window.open GET
 * would not).
 */
export const FileCell = ({
  fileGuid,
  isEditing,
  disabled,
  onBusyChange,
  metadata,
  onValueChange,
}: FileCellProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [getDirectory] = useLazyGetDirectoryQuery();
  const [uploadFieldFile] = useUploadFieldFileMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();

  useEffect(() => {
    onBusyChange?.(isUploading || isDeleting);
    return () => onBusyChange?.(false);
  }, [isUploading, isDeleting, onBusyChange]);

  if (!isEditing) {
    if (!fileGuid) return null;
    return <FileFieldActions fileGuid={fileGuid} fallbackName="file" />;
  }

  const rawFolder = metadata?.folder;
  const uploadFolder =
    typeof rawFolder === 'string' && rawFolder.trim() !== ''
      ? rawFolder.trim()
      : DEFAULT_UPLOAD_FOLDER;

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onValueChange || disabled) return;

    setUploadError(null);
    setIsUploading(true);
    try {
      const destinationPathInfo = await getDirectory({ dirName: uploadFolder }).unwrap();
      const newGuid = await uploadFieldFile({ file, destinationPathInfo }).unwrap();
      onValueChange(newGuid);
    } catch (error) {
      setUploadError(extractErrorMessage(error, 'Failed to upload file'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!fileGuid || !onValueChange || disabled) return;
    setUploadError(null);
    try {
      await deleteFile({ fileId: fileGuid }).unwrap();
      onValueChange(null);
    } catch (error) {
      setUploadError(extractErrorMessage(error, 'Failed to delete file'));
    }
  };

  if (fileGuid) {
    return (
      <FileFieldActions
        fileGuid={fileGuid}
        fallbackName="file"
        disabled={disabled || isDeleting}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={getAcceptAttribute(metadata)}
        onChange={handleFileSelected}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={isUploading ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isUploading}
      >
        upload
      </Button>
      {uploadError && <Alert severity="error">{uploadError}</Alert>}
    </Box>
  );
};
